(function (root, factory) {
  const createPageTrackingCore = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = createPageTrackingCore;
    return;
  }

  if (root) {
    root.createPageTrackingCore = createPageTrackingCore;
  }
})(typeof window !== 'undefined' ? window : undefined, function createPageTrackingCoreFactory() {
  return function createPageTrackingCore(options) {
    const currentOptions = options || {};
    const windowObject = currentOptions.window;
    const documentObject = currentOptions.document;
    const config = currentOptions.config || {};
    const trackingContext = currentOptions.trackingContext || {};
    const utils = currentOptions.utils;
    const navigatorObject = windowObject.navigator || {};
    const fetchFunction = typeof windowObject.fetch === 'function'
      ? windowObject.fetch.bind(windowObject)
      : (typeof fetch === 'function' ? fetch : null);

    const sessionIdKey = 'pageTrackingSessionId';
    const sessionStartAtKey = 'pageTrackingSessionStartAt';
    const lastActiveAtKey = 'pageTrackingLastActiveAt';
    const endedSessionIdKey = 'pageTrackingEndedSessionId';
    const pendingPageJumpKey = 'pageTrackingPendingPageJump';
    const pendingSearchKey = 'pageTrackingPendingSearch';
    const hasVisitedKey = 'pageTrackingHasVisited';
    const anonymousUserIdKey = 'pageTrackingAnonymousUserId';
    // 用户无活动后，判定为 session 失效的超时时间（毫秒），30 分钟
    const inactivityTimeoutMs = 30 * 60 * 1000;
    // pendingPageJumpMaxAgeMs 用于限制 pendingPageJump 的最大有效保存时间（毫秒），当前设置为 10 秒
    const pendingPageJumpMaxAgeMs = 10 * 1000;
    const memoryStore = {};
    const state = {
      reportedScrollDepths: [],
      maxScrollDepth: 0,
      initialExposureSent: false
    };

    function canTrackConfig() {
      return Boolean(
        utils &&
        config.enabled &&
        config.basicUserId &&
        config.endpoint &&
        config.projectId
      );
    }

    function getCustomerPrivacy() {
      const shopifyObject = windowObject.Shopify;

      if (!shopifyObject || !shopifyObject.customerPrivacy) {
        return null;
      }

      return shopifyObject.customerPrivacy;
    }

    function hasTrackingConsent() {
      const customerPrivacy = getCustomerPrivacy();

      if (!customerPrivacy || typeof customerPrivacy.userCanBeTracked !== 'function') {
        return false;
      }

      try {
        console.log('TODO page-tracking-core 是否允许上报', customerPrivacy.userCanBeTracked(), customerPrivacy.getTrackingConsent());
        return customerPrivacy.userCanBeTracked() === true;
      } catch (error) {
        return false;
      }
    }

    function canTrack() {
      return canTrackConfig() && hasTrackingConsent();
    }

    function readStorageArea(storageName, key) {
      let storageValue = '';
      const storageObject = windowObject[storageName];

      try {
        storageValue = storageObject.getItem(key) || '';
      } catch (error) {
        storageValue = '';
      }

      if (storageValue) {
        memoryStore[key] = storageValue;
        return storageValue;
      }

      return memoryStore[key] || '';
    }

    function writeStorageArea(storageName, key, value) {
      const normalizedValue = String(value);
      const storageObject = windowObject[storageName];

      memoryStore[key] = normalizedValue;

      try {
        storageObject.setItem(key, normalizedValue);
      } catch (error) {
        return false;
      }

      return true;
    }

    function readSessionStorage(key) {
      return readStorageArea('sessionStorage', key);
    }

    function writeSessionStorage(key, value) {
      return writeStorageArea('sessionStorage', key, value);
    }

    function readPersistentStorage(key) {
      return readStorageArea('localStorage', key);
    }

    function writePersistentStorage(key, value) {
      return writeStorageArea('localStorage', key, value);
    }

    function createSessionId() {
      if (windowObject.crypto && typeof windowObject.crypto.randomUUID === 'function') {
        return windowObject.crypto.randomUUID();
      }

      return 'session-' + Date.now() + '-' + Math.random().toString(16).slice(2);
    }

    function parseTimestampValue(value) {
      const numericValue = Number(value);

      if (Number.isNaN(numericValue) || numericValue <= 0) {
        return 0;
      }

      return numericValue;
    }

    function shouldStartNewSession(nowMs) {
      const sessionId = readSessionStorage(sessionIdKey);
      const lastActiveAt = parseTimestampValue(readSessionStorage(lastActiveAtKey));
      const endedSessionId = readSessionStorage(endedSessionIdKey);

      return !sessionId ||
        !lastActiveAt ||
        sessionId === endedSessionId ||
        nowMs - lastActiveAt > inactivityTimeoutMs;
    }

    function getSessionId() {
      return readSessionStorage(sessionIdKey);
    }

    function getSessionStartAt() {
      return parseTimestampValue(readSessionStorage(sessionStartAtKey));
    }

    function getLastActiveAt() {
      return parseTimestampValue(readSessionStorage(lastActiveAtKey));
    }

    function getEndedSessionId() {
      return readSessionStorage(endedSessionIdKey);
    }

    function clearEndedSessionId(sessionId) {
      if (!sessionId || getEndedSessionId() === sessionId) {
        writeSessionStorage(endedSessionIdKey, '');
      }
    }

    function markActivity(nowMs) {
      writeSessionStorage(lastActiveAtKey, String(nowMs || Date.now()));
    }

    function getLoggedInUserId() {
      return utils.fillEmptyValue(trackingContext.userId);
    }

    function getAnonymousUserId() {
      return readPersistentStorage(anonymousUserIdKey);
    }

    function createAnonymousUserId() {
      const prefix = 'anonymous-';
      if (windowObject.crypto && typeof windowObject.crypto.randomUUID === 'function') {
        return prefix + windowObject.crypto.randomUUID();
      }

      return prefix + Date.now() + '-' + Math.random().toString(16).slice(2);
    }

    function ensureAnonymousUserId() {
      const existingAnonymousUserId = getAnonymousUserId();
      let nextAnonymousUserId;

      if (existingAnonymousUserId) {
        return {
          userId: existingAnonymousUserId,
          isNewUser: false
        };
      }

      nextAnonymousUserId = createAnonymousUserId();
      writePersistentStorage(anonymousUserIdKey, nextAnonymousUserId);

      return {
        userId: nextAnonymousUserId,
        isNewUser: true
      };
    }

    function resolveTrackingUser() {
      const loggedInUserId = getLoggedInUserId();
      const anonymousUserResult = ensureAnonymousUserId();

      return {
        dataId: anonymousUserResult.userId,
        loginDataId: loggedInUserId,
        isNewUser: loggedInUserId ? false : anonymousUserResult.isNewUser
      };
    }

    function buildBasicInfo() {
      return utils.buildBasicInfo({
        userId: config.basicUserId,
        projectId: config.projectId
      });
    }

    function buildDataInfo(fields, now) {
      const eventTime = now instanceof Date ? now : new Date(now || Date.now());
      const environment = utils.detectClientEnvironment({
        userAgent: navigatorObject.userAgent || '',
        innerWidth: windowObject.innerWidth || 0,
        language: trackingContext.language || navigatorObject.language || ''
      });
      const trackingUser = resolveTrackingUser();

      return utils.buildDataInfo(Object.assign({
        sessionId: getSessionId(),
        shopName: trackingContext.shopName || '',
        dataId: trackingUser.dataId,
        loginDataId: trackingUser.loginDataId,
        deviceType: environment.deviceType,
        os: environment.os,
        browser: environment.browser,
        timestamp: utils.formatTimestamp(eventTime),
        pageUrl: windowObject.location && windowObject.location.href ? windowObject.location.href : '',
        referrer: documentObject.referrer || '',
        userCountry: trackingContext.userCountry,
        // shopCountry: trackingContext.shopCountry,
        language: environment.language
      }, fields || {}));
    }

    function createRequestBody(payload) {
      const requestBody = new URLSearchParams();
      requestBody.set('basicInfo', JSON.stringify(payload.basicInfo || {}));
      requestBody.set('dataInfo', JSON.stringify(payload.dataInfo || {}));

      return requestBody.toString();
    }

    function sendTrackingEvent(payload, useBeacon) {
      // console.log('111sendTrackingEvent', payload.dataInfo.type, useBeacon, payload);
      if (!canTrack()) {
        return Promise.resolve(false);
      }

      const requestBody = createRequestBody(payload);

      if (useBeacon && typeof navigatorObject.sendBeacon === 'function') {
        try {
          let beaconResult;
          const beaconBody = new Blob([requestBody], {
            type: 'application/x-www-form-urlencoded;charset=UTF-8'
          });

          beaconResult = navigatorObject.sendBeacon(config.endpoint, beaconBody);

          if (beaconResult) {
            return beaconResult;
          }
        } catch (error) {
          // Fall through to fetch keepalive when beacon construction fails.
        }
      }

      if (!fetchFunction) {
        return Promise.resolve(false);
      }

      return fetchFunction(config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: requestBody,
        keepalive: Boolean(useBeacon)
      }).catch(function handleRequestError() {
        return false;
      });
    }

    return {
      window: windowObject,
      document: documentObject,
      config,
      trackingContext,
      utils,
      navigatorObject,
      fetchFunction,
      state,
      sessionIdKey,
      sessionStartAtKey,
      lastActiveAtKey,
      endedSessionIdKey,
      pendingPageJumpKey,
      pendingSearchKey,
      hasVisitedKey,
      anonymousUserIdKey,
      inactivityTimeoutMs,
      pendingPageJumpMaxAgeMs,
      canTrackConfig,
      getCustomerPrivacy,
      hasTrackingConsent,
      canTrack,
      readStorageArea,
      writeStorageArea,
      readSessionStorage,
      writeSessionStorage,
      readPersistentStorage,
      writePersistentStorage,
      createSessionId,
      parseTimestampValue,
      shouldStartNewSession,
      getSessionId,
      getSessionStartAt,
      getLastActiveAt,
      getEndedSessionId,
      clearEndedSessionId,
      markActivity,
      getLoggedInUserId,
      getAnonymousUserId,
      createAnonymousUserId,
      ensureAnonymousUserId,
      resolveTrackingUser,
      buildBasicInfo,
      buildDataInfo,
      createRequestBody,
      sendTrackingEvent
    };
  };
});
